"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { usePathnameSegments } from "@/hooks/use-pathname-segments";
import { useLayoutStore } from "@/stores/layout-store";
import { showToast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "@/components/ui/icon";

const STATIC_LABELS: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/projects": "Projects",
  "/jobs": "Jobs",
  "/tasks": "Tasks",
  "/talent-search": "Talent Search",
  "/templates": "Templates",
  "/presets": "Presets",
  "/snippets": "Snippets",
  "/network": "Team network",
  "/settings": "Settings",
  "/notifications": "Notifications",
  // Decision 176: the UI says "Meetings"; only code paths say "conversations".
  "/conversations": "Meetings",
  "/settings/notetaker": "Notetaker",
  "/settings/my-conversations": "My meetings",
};

/** Section glyph shown before the trail — mirrors the sidebar nav icons. */
const SECTION_ICONS: Record<string, IconName> = {
  "/dashboard": "IconLayoutDashboard",
  "/projects": "IconFolderBookmarks",
  "/jobs": "IconBag",
  "/tasks": "IconSquareChecklist",
  "/talent-search": "IconSearchIntelligence",
  "/templates": "IconPaperPlane",
  "/presets": "IconLayoutDashboard",
  "/snippets": "IconScissors2",
  "/network": "IconHandshake",
  "/settings": "IconSettingsGear2",
  "/notifications": "IconBell2",
  "/conversations": "IconVideo",
};

const SEGMENT_LABELS: Record<string, string> = {
  sequences: "Sequences",
  pipeline: "Pipeline",
  arbitrator: "Arbitrator",
  enroll: "Enroll",
  preview: "Preview",
  employers: "Employers",
  // `/jobs/{id}/conversations` tab segment (decision 176).
  conversations: "Meetings",
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PathPart {
  label: string;
  href: string;
  isUuid: boolean;
  hasCustomLabel: boolean;
  editable: boolean;
  onRename?: (nextLabel: string) => Promise<void> | void;
}

export function Breadcrumbs() {
  const { segments } = usePathnameSegments();
  const breadcrumbs = useLayoutStore((s) => s.breadcrumbs);

  const crumbs = useMemo<PathPart[]>(() => {
    if (segments.length === 0) return [];

    return segments.map((segment, i) => {
      const href = "/" + segments.slice(0, i + 1).join("/");
      const isUuid = UUID_REGEX.test(segment);
      let hasCustomLabel = false;

      let label = STATIC_LABELS[href] ?? SEGMENT_LABELS[segment];

      const customVal = breadcrumbs[segment];
      let editable = false;
      let onRename: PathPart["onRename"] = undefined;

      if (customVal) {
        hasCustomLabel = true;
        if (typeof customVal === "string") {
          label = customVal;
        } else {
          label = customVal.label;
          editable = !!customVal.editable;
          onRename = customVal.onRename;
        }
      }

      if (!label) {
        label = segment.replace(/[-_]/g, " ");
      }

      return { label, href, isUuid, hasCustomLabel, editable, onRename };
    });
  }, [segments, breadcrumbs]);

  const [editingHref, setEditingHref] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingHref, setSavingHref] = useState<string | null>(null);

  const submitRename = async (crumb: PathPart, nextValue: string) => {
    const trimmedValue = nextValue.trim();
    setEditingHref(null);
    if (!trimmedValue || trimmedValue === crumb.label || !crumb.onRename) {
      return;
    }
    setSavingHref(crumb.href);
    try {
      await showToast.promise(
        () => Promise.resolve(crumb.onRename?.(trimmedValue)),
        {
          loading: "Saving search name...",
          success: "Search name updated",
          error: "Couldn't update search name",
        },
      );
    } finally {
      setSavingHref(null);
    }
  };

  const lastCrumb = crumbs[crumbs.length - 1];
  const sectionIcon = crumbs[0] ? SECTION_ICONS[crumbs[0].href] : undefined;
  const ancestors = crumbs.slice(0, -1);

  const renderCrumb = (
    crumb: PathPart,
    isLast: boolean,
    showSeparator: boolean,
  ) => (
    <div key={crumb.href} className="flex min-w-0 items-center gap-0.5">
      {showSeparator && (
        <div className="shrink-0 opacity-30">
          <Icon
            name="IconChevronRightSmall"
            size={14}
            className="text-heading"
          />
        </div>
      )}

      {crumb.isUuid && !crumb.hasCustomLabel ? (
        <div className="h-4 w-24 rounded bg-contrast/10 shimmer shimmer-bg text-foreground" />
      ) : isLast && crumb.editable && editingHref === crumb.href ? (
        <Input
          type="text"
          variant="ghost"
          size="none"
          autoFocus
          value={editingValue}
          onChange={(event) => setEditingValue(event.target.value)}
          onBlur={async () => submitRename(crumb, editingValue)}
          onKeyDown={async (event) => {
            if (event.key === "Escape") {
              setEditingHref(null);
              setEditingValue("");
              return;
            }
            if (event.key !== "Enter") return;
            event.preventDefault();
            await submitRename(crumb, editingValue);
          }}
          className="h-7 w-40 rounded-md border border-contrast/15 bg-contrast/3 px-2 text-sm font-medium leading-5 tracking-[-0.13px]"
        />
      ) : isLast ? (
        <button
          type="button"
          onClick={() => {
            if (!crumb.editable || savingHref === crumb.href) {
              return;
            }
            setEditingHref(crumb.href);
            setEditingValue(crumb.label);
          }}
          className={cn(
            "group/crumb flex items-center gap-1 text-sm font-medium leading-5 tracking-[-0.13px] max-w-60 truncate capitalize",
            crumb.editable
              ? "text-heading hover:text-contrast/80 transition-colors cursor-text"
              : "text-heading",
          )}
        >
          <span className="truncate">{crumb.label}</span>
          {crumb.editable && savingHref !== crumb.href && (
            <Icon
              name="IconPencil"
              size={12}
              className="text-foreground opacity-0 group-hover/crumb:opacity-40 transition-opacity shrink-0"
            />
          )}
        </button>
      ) : (
        <Link
          href={crumb.href}
          className="text-sm font-medium leading-5 text-contrast/50 tracking-[-0.13px] max-w-[200px] truncate capitalize hover:text-contrast/70 transition-colors"
        >
          {crumb.label}
        </Link>
      )}
    </div>
  );

  return (
    <div className="min-w-0 flex flex-1 items-center gap-1.5">
      {sectionIcon && (
        <Icon
          name={sectionIcon}
          size={16}
          aria-hidden
          className="shrink-0 text-contrast/60"
        />
      )}

      {lastCrumb && (
        <span className="lg:hidden text-sm font-medium leading-5 text-heading tracking-[-0.13px] truncate capitalize">
          {lastCrumb.label}
        </span>
      )}

      <div className="hidden lg:flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
        {/* Beside the header's controls the ancestors get what the current
            page's crumb leaves, and truncate first. */}
        {ancestors.length > 0 && (
          <div className="flex min-w-0 max-w-fit flex-1 items-center gap-0.5 overflow-hidden">
            {ancestors.map((crumb, i) => renderCrumb(crumb, false, i > 0))}
          </div>
        )}
        {lastCrumb && renderCrumb(lastCrumb, true, ancestors.length > 0)}
      </div>
    </div>
  );
}
